import { environment } from '../config/environment';

/** API host origin, e.g. https://console.dietmaster.fit — static files often live here, not under /api/customer/. */
function getApiOrigin(): string {
  const raw = environment.API_URL?.trim() ?? '';
  if (!raw) {return '';}
  const with_scheme = raw.startsWith('http') ? raw : `https://${raw}`;
  const m = with_scheme.match(/^(https?:)\/\/([^/?#]+)/i);
  if (!m) {return '';}
  return `${m[1]}//${m[2]}`;
}

/**
 * First <img src> or <img data-src> in HTML (CMS often only puts images in body HTML).
 */
export function extractFirstImageSrcFromHtml(html: string | null | undefined): string | null {
  if (!html || typeof html !== 'string') {return null;}
  const img = html.match(/<img[^>]+(?:src|data-src)\s*=\s*["']([^"']+)["']/i);
  return img?.[1]?.trim() ?? null;
}

/**
 * Turn CMS / pages API image values into an absolute URL.
 * - Full http(s) and protocol-relative // URLs pass through (fixed).
 * - Paths like storage/..., uploads/... usually map to API **origin**, not .../api/customer/storage/...
 */
export function resolveCmsImageUri(url: string | null | undefined): string | null {
  if (url == null || typeof url !== 'string') {return null;}
  let trimmed = url.trim();
  if (!trimmed) {return null;}

  if (trimmed.startsWith('//')) {
    trimmed = `https:${trimmed}`;
  }
  /** CMS often embeds inline images in HTML; must not prefix with API base. */
  if (/^data:image\//i.test(trimmed)) {
    return trimmed;
  }
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }

  const path = trimmed.startsWith('/') ? trimmed.slice(1) : trimmed;
  const base = environment.API_URL?.replace(/\/$/, '') ?? '';
  const origin = getApiOrigin();

  const looks_like_static_upload =
    /^(storage\/|uploads\/|public\/|media\/|images\/)/i.test(path) ||
    path.includes('/storage/') ||
    path.includes('/uploads/');

  if (origin && looks_like_static_upload) {
    return `${origin}/${path}`;
  }

  return `${base}/${path}`;
}

const DIRECT_KEYS = [
  'image',
  'featured_image',
  'thumbnail',
  'banner',
  'cover_image',
  'photo',
  'hero_image',
  'featured_image_url',
  'image_url',
  'attachment',
  'attachments',
  'img',
  'picture',
  'file',
  'file_url',
  'cover',
];

function tryResolveString(raw: string | undefined | null): string | null {
  if (raw == null || typeof raw !== 'string') {return null;}
  return resolveCmsImageUri(raw);
}

function firstImageFromArray(arr: unknown): string | null {
  if (!Array.isArray(arr) || arr.length === 0) {return null;}
  for (const item of arr) {
    if (typeof item === 'string') {
      const r = tryResolveString(item);
      if (r) {return r;}
    }
    if (item && typeof item === 'object' && item !== null) {
      const o = item as Record<string, unknown>;
      for (const k of ['url', 'src', 'path', 'image', 'file']) {
        const r = tryResolveString(typeof o[k] === 'string' ? (o[k] as string) : null);
        if (r) {return r;}
      }
    }
  }
  return null;
}

/**
 * Pick first usable image URL from a CMS page object (pages API field names vary by admin).
 */
export function pickCmsPageImageUri(page: Record<string, unknown> | null | undefined): string | null {
  if (!page || typeof page !== 'object') {return null;}

  for (const k of DIRECT_KEYS) {
    const v = page[k];
    if (typeof v === 'string') {
      const r = tryResolveString(v);
      if (r) {return r;}
      const extracted = extractFirstImageSrcFromHtml(v);
      if (extracted) {
        const from_html = tryResolveString(extracted);
        if (from_html) {return from_html;}
      }
    }
    if (v && typeof v === 'object' && 'url' in v && typeof (v as { url?: string }).url === 'string') {
      const r = tryResolveString((v as { url: string }).url);
      if (r) {return r;}
    }
  }

  for (const arrKey of ['images', 'attachments', 'media', 'gallery']) {
    const r = firstImageFromArray(page[arrKey]);
    if (r) {return r;}
  }

  for (const htmlKey of ['content', 'a_content', 'excerpt', 'meta_description', 'description']) {
    const html = page[htmlKey];
    if (typeof html === 'string') {
      const extracted = extractFirstImageSrcFromHtml(html);
      if (extracted) {
        const r = tryResolveString(extracted);
        if (r) {return r;}
      }
    }
  }

  for (const [, v] of Object.entries(page)) {
    if (typeof v === 'string') {
      const s = v.trim();
      if (/^https?:\/\/.+\.(jpe?g|png|gif|webp|bmp)(\?[^#]*)?$/i.test(s)) {
        return s;
      }
    }
  }

  return null;
}
